import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { unionBy } from 'lodash';
import { EMPTY, of } from 'rxjs';
import { filter, finalize, switchMap, tap } from 'rxjs/operators';

import { untilDestroyed } from '@app/core';
import { OAuthClient, User } from '@app/models';
import { ErrorHandlerService, NotyService, OAuthService, UserService } from '@app/services';
import { AlertModalService } from '@app/shared';

import { ModalStep as OAuthClientModalStep } from './oauth-client-modal/oauth-client-modal.component';
import { OAuthClientModalService } from './oauth-client-modal/oauth-client-modal.service';
import { OAuthClientCredentialsModalService } from './oauth-client-credentials-modal/oauth-client-credentials-modal.service';

@Component({
  selector: 'app-configurations-oauth-clients',
  templateUrl: './configurations-oauth-clients.component.html',
  styleUrls: ['../configurations.scss', './configurations-oauth-clients.component.scss'],
  standalone: false
})
export class ConfigurationsOAuthClientsComponent implements OnInit, OnDestroy {
  currentUser: User;
  oauthClients: OAuthClient[] = [];
  isLoading = true;
  isLastPage = false;
  pageNumber = 1;
  readonly perPage = 30;

  constructor(
    private router: Router,
    private errorHandlerService: ErrorHandlerService,
    private notyService: NotyService,
    private oauthService: OAuthService,
    private userService: UserService,
    private translateService: TranslateService,
    private alertModalService: AlertModalService,
    private oauthClientModalService: OAuthClientModalService,
    private oauthClientCredentialsModalService: OAuthClientCredentialsModalService
  ) {}

  ngOnInit() {
    this.userService
      .watchCurrentUser()
      .pipe(
        filter(user => !!user),
        untilDestroyed(this)
      )
      .subscribe({
        next: user => (this.currentUser = user),
        error: error => this.errorHandlerService.handle(error)
      });

    this.userService
      .getCurrentUser({ fetchPolicy: 'cache-first' })
      .pipe(
        switchMap(user => {
          if (!user.currentPermissions?.actions_applications_oz) {
            this.notyService.error(this.translateService.instant('error_without_permission'));
            this.router.navigate(['/perfil']);

            return EMPTY;
          }

          return user.settings?.developer ? of(user) : this.userService.updateCurrentUserDeveloperFlag({ developer: true });
        })
      )
      .subscribe({
        next: () => this.loadOAuthClients(),
        error: error => {
          this.isLoading = false;
          this.errorHandlerService.handle(error);
        }
      });
  }

  ngOnDestroy() {}

  loadOAuthClients(options: { page?: number } = {}) {
    this.isLoading = true;
    this.pageNumber = options.page || 1;

    this.oauthService
      .clients({ limit: this.perPage, page: this.pageNumber })
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe({
        next: page => {
          const clients = (page?.data || []).filter(client => !!client);

          this.oauthClients = unionBy(this.pageNumber === 1 ? [] : this.oauthClients, clients, 'id');
          this.isLastPage = !page || !page.has_more_pages;
        },
        error: error => this.errorHandlerService.handle(error)
      });
  }

  openCreateOAuthClientModal() {
    this.oauthClientModalService.open().subscribe({
      next: client => {
        if (client) {
          this.upsertOAuthClient({ ...client, client_secret: null });

          if (client.client_secret) {
            this.oauthClientCredentialsModalService.open(client).subscribe();
          }
        }
      }
    });
  }

  openOAuthBasicInfoModal(client: OAuthClient) {
    this.oauthClientModalService.open({ client, step: OAuthClientModalStep.basicInfo }).subscribe({
      next: updatedClient => this.upsertOAuthClient(updatedClient)
    });
  }

  openOAuthAuthenticationModal(client: OAuthClient) {
    this.oauthClientModalService.open({ client, step: OAuthClientModalStep.authentication }).subscribe({
      next: updatedClient => this.upsertOAuthClient(updatedClient)
    });
  }

  deleteOAuthClient(client: OAuthClient) {
    this.alertModalService
      .warning({
        text: this.translateService.instant('alerts.deleteOauthClient'),
        confirmButtonText: this.translateService.instant('button.exclude')
      })
      .pipe(
        tap(() => (this.isLoading = true)),
        switchMap(() => this.oauthService.revokeClient({ id: client.id })),
        finalize(() => (this.isLoading = false))
      )
      .subscribe({
        next: () => {
          this.oauthClients = this.oauthClients.filter(oauthClient => oauthClient.id !== client.id);
          this.notyService.success(this.translateService.instant('settings.oauthClients.deletedWithSuccess'));
        },
        error: error => this.errorHandlerService.handle(error)
      });
  }

  private upsertOAuthClient(client: OAuthClient) {
    const existingIndex = this.oauthClients.findIndex(item => item.id === client.id);

    if (existingIndex === -1) {
      this.oauthClients = [client].concat(this.oauthClients);
      return;
    }

    const updatedClient = {
      ...client,
      icon_url: client.icon_url ? `${client.icon_url.split('?')[0]}?t=${Date.now()}` : null
    };

    this.oauthClients = this.oauthClients.map(item => (item.id === client.id ? updatedClient : item));
  }
}
